import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ClientFleetCompliance, ClientWorkOrder, ClientPMSchedule, ClientDVIR, ComplianceAlert, InspectionAttachmentSummary } from '@numeo/types-core'
import { FleetOverviewData, FleetEquipmentResponse, TrailerStatsResponse } from '../services/fleetApi'
import { RootState } from 'store'

export interface FleetState {
    overview: { data: FleetOverviewData | null; loading: boolean; error: string | null }
    equipment: { data: FleetEquipmentResponse | null; loading: boolean; error: string | null }
    compliance: { records: ClientFleetCompliance[]; loading: boolean; error: string | null }
    complianceAlerts: { data: ComplianceAlert[]; loading: boolean; error: string | null }
    workOrders: { items: ClientWorkOrder[]; loading: boolean; error: string | null }
    pmSchedules: { items: ClientPMSchedule[]; loading: boolean; error: string | null }
    dvirs: { items: ClientDVIR[]; loading: boolean; error: string | null }
    trailerStats: { data: TrailerStatsResponse | null; loading: boolean; error: string | null }
}

const initialState: FleetState = {
    overview: { data: null, loading: false, error: null },
    equipment: { data: null, loading: false, error: null },
    compliance: { records: [], loading: false, error: null },
    complianceAlerts: { data: [], loading: false, error: null },
    workOrders: { items: [], loading: false, error: null },
    pmSchedules: { items: [], loading: false, error: null },
    dvirs: { items: [], loading: false, error: null },
    trailerStats: { data: null, loading: false, error: null },
}

const fleetSlice = createSlice({
    name: 'fleet',
    initialState,
    reducers: {
        // Overview
        setOverviewLoading(state, action: PayloadAction<boolean>) {
            state.overview.loading = action.payload
        },
        setOverviewData(state, action: PayloadAction<FleetOverviewData>) {
            state.overview.data = action.payload
            state.overview.loading = false
            state.overview.error = null
        },
        setOverviewError(state, action: PayloadAction<string>) {
            state.overview.error = action.payload
            state.overview.loading = false
        },

        // Equipment
        setEquipmentLoading(state, action: PayloadAction<boolean>) {
            state.equipment.loading = action.payload
        },
        setEquipmentData(state, action: PayloadAction<FleetEquipmentResponse>) {
            state.equipment.data = action.payload
            state.equipment.loading = false
            state.equipment.error = null
        },
        setEquipmentError(state, action: PayloadAction<string>) {
            state.equipment.error = action.payload
            state.equipment.loading = false
        },

        // Compliance
        setComplianceLoading(state, action: PayloadAction<boolean>) {
            state.compliance.loading = action.payload
        },
        setComplianceRecords(state, action: PayloadAction<ClientFleetCompliance[]>) {
            state.compliance.records = action.payload
            state.compliance.loading = false
            state.compliance.error = null
        },
        setComplianceError(state, action: PayloadAction<string>) {
            state.compliance.error = action.payload
            state.compliance.loading = false
        },

        // Compliance Alerts
        setComplianceAlertsLoading(state, action: PayloadAction<boolean>) {
            state.complianceAlerts.loading = action.payload
        },
        setComplianceAlerts(state, action: PayloadAction<ComplianceAlert[]>) {
            state.complianceAlerts.data = action.payload
            state.complianceAlerts.loading = false
            state.complianceAlerts.error = null
        },
        setComplianceAlertsError(state, action: PayloadAction<string>) {
            state.complianceAlerts.error = action.payload
            state.complianceAlerts.loading = false
        },

        // Work Orders
        setWorkOrdersLoading(state, action: PayloadAction<boolean>) {
            state.workOrders.loading = action.payload
        },
        setWorkOrders(state, action: PayloadAction<ClientWorkOrder[]>) {
            state.workOrders.items = action.payload
            state.workOrders.loading = false
            state.workOrders.error = null
        },
        upsertWorkOrder(state, action: PayloadAction<ClientWorkOrder>) {
            const index = state.workOrders.items.findIndex((order) => order.id === action.payload.id)
            if (index < 0) state.workOrders.items.unshift(action.payload)
            else state.workOrders.items[index] = action.payload
        },
        setWorkOrdersError(state, action: PayloadAction<string>) {
            state.workOrders.error = action.payload
            state.workOrders.loading = false
        },

        // PM Schedules
        setPMSchedulesLoading(state, action: PayloadAction<boolean>) {
            state.pmSchedules.loading = action.payload
        },
        setPMSchedules(state, action: PayloadAction<ClientPMSchedule[]>) {
            state.pmSchedules.items = action.payload
            state.pmSchedules.loading = false
            state.pmSchedules.error = null
        },
        setPMSchedulesError(state, action: PayloadAction<string>) {
            state.pmSchedules.error = action.payload
            state.pmSchedules.loading = false
        },

        // DVIRs
        setDVIRsLoading(state, action: PayloadAction<boolean>) {
            state.dvirs.loading = action.payload
        },
        setDVIRs(state, action: PayloadAction<ClientDVIR[]>) {
            state.dvirs.items = action.payload
            state.dvirs.loading = false
            state.dvirs.error = null
        },
        setDVIRsError(state, action: PayloadAction<string>) {
            state.dvirs.error = action.payload
            state.dvirs.loading = false
        },
        // An attachment upload/remove already returns the updated list, so patch that one DVIR instead of refetching all of them.
        updateDVIRAttachments(state, action: PayloadAction<{ id: string; attachments: InspectionAttachmentSummary[] }>) {
            const dvir = state.dvirs.items.find((item) => item.id === action.payload.id)
            if (dvir) dvir.attachments = action.payload.attachments
        },

        // Trailer Stats
        setTrailerStatsLoading(state, action: PayloadAction<boolean>) {
            state.trailerStats.loading = action.payload
        },
        setTrailerStatsData(state, action: PayloadAction<TrailerStatsResponse>) {
            state.trailerStats.data = action.payload
            state.trailerStats.loading = false
            state.trailerStats.error = null
        },
        setTrailerStatsError(state, action: PayloadAction<string>) {
            state.trailerStats.error = action.payload
            state.trailerStats.loading = false
        },
    },
})

export const {
    setOverviewLoading,
    setOverviewData,
    setOverviewError,
    setEquipmentLoading,
    setEquipmentData,
    setEquipmentError,
    setComplianceLoading,
    setComplianceRecords,
    setComplianceError,
    setComplianceAlertsLoading,
    setComplianceAlerts,
    setComplianceAlertsError,
    setWorkOrdersLoading,
    setWorkOrders,
    upsertWorkOrder,
    setWorkOrdersError,
    setPMSchedulesLoading,
    setPMSchedules,
    setPMSchedulesError,
    setDVIRsLoading,
    setDVIRs,
    setDVIRsError,
    updateDVIRAttachments,
    setTrailerStatsLoading,
    setTrailerStatsData,
    setTrailerStatsError,
} = fleetSlice.actions

// Selectors
export const selectFleetOverview = (state: RootState) => state.fleet.overview
export const selectFleetEquipment = (state: RootState) => state.fleet.equipment
export const selectFleetCompliance = (state: RootState) => state.fleet.compliance
export const selectFleetComplianceAlerts = (state: RootState) => state.fleet.complianceAlerts
export const selectFleetWorkOrders = (state: RootState) => state.fleet.workOrders
export const selectFleetPMSchedules = (state: RootState) => state.fleet.pmSchedules
export const selectFleetDVIRs = (state: RootState) => state.fleet.dvirs
export const selectTrailerStats = (state: RootState) => state.fleet.trailerStats

export default fleetSlice.reducer
